import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Server-synchronized timer hook that prevents timer issues when browser tab is hidden/minimized
 * @param {number} initialDuration - Initial timer duration in seconds
 * @param {function} onTimeUp - Callback function when timer reaches 0
 * @param {boolean} autoStart - Whether to start timer immediately
 * @returns {object} Timer state and controls
 */
export const useServerSyncTimer = (initialDuration = 60, onTimeUp = null, autoStart = true) => {
  const [timeLeft, setTimeLeft] = useState(initialDuration);
  const [isActive, setIsActive] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  
  // Store game start time and duration
  const gameStartTimeRef = useRef(null);
  const gameDurationRef = useRef(initialDuration * 1000); // Convert to milliseconds
  const intervalRef = useRef(null);
  const onTimeUpRef = useRef(onTimeUp);

  // Update callback ref when it changes
  useEffect(() => {
    onTimeUpRef.current = onTimeUp;
  }, [onTimeUp]);

  // Calculate remaining time based on real elapsed time
  const calculateTimeLeft = useCallback(() => {
    if (!gameStartTimeRef.current || isPaused) {
      return timeLeft;
    }

    const elapsed = Date.now() - gameStartTimeRef.current;
    const remaining = Math.max(0, gameDurationRef.current - elapsed);
    return Math.ceil(remaining / 1000); // Convert back to seconds
  }, [isPaused, timeLeft]);

  // Start timer
  const startTimer = useCallback((duration = null) => {
    if (duration) {
      gameDurationRef.current = duration * 1000;
      setTimeLeft(duration);
    }
    
    gameStartTimeRef.current = Date.now();
    setIsActive(true);
    setIsPaused(false);
  }, []);

  // Pause timer
  const pauseTimer = useCallback(() => {
    setIsPaused(true);
    setIsActive(false);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
  }, []);

  // Resume timer
  const resumeTimer = useCallback(() => {
    if (gameStartTimeRef.current && isPaused) {
      // Recalculate remaining time and adjust start time
      const currentTimeLeft = calculateTimeLeft();
      gameStartTimeRef.current = Date.now() - (gameDurationRef.current - currentTimeLeft * 1000);
      setIsPaused(false);
      setIsActive(true);
    }
  }, [isPaused, calculateTimeLeft]);

  // Reset timer
  const resetTimer = useCallback((newDuration = null) => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    
    const duration = newDuration || (gameDurationRef.current / 1000);
    gameDurationRef.current = duration * 1000;
    setTimeLeft(duration);
    setIsActive(false);
    setIsPaused(false);
    gameStartTimeRef.current = null;
  }, []);

  // Handle page visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && isActive && !isPaused) {
        // User returned - recalculate time based on real elapsed time
        const newTimeLeft = calculateTimeLeft();
        setTimeLeft(newTimeLeft);
        
        // If time is up, trigger callback
        if (newTimeLeft <= 0 && onTimeUpRef.current) {
          onTimeUpRef.current();
          setIsActive(false);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isActive, isPaused, calculateTimeLeft]);

  // Main timer effect
  useEffect(() => {
    if (isActive && !isPaused) {
      intervalRef.current = setInterval(() => {
        const newTimeLeft = calculateTimeLeft();
        setTimeLeft(newTimeLeft);

        // Check if time is up
        if (newTimeLeft <= 0) {
          setIsActive(false);
          if (onTimeUpRef.current) {
            onTimeUpRef.current();
          }
        }
      }, 1000);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isActive, isPaused, calculateTimeLeft]);

  // Auto-start if enabled
  useEffect(() => {
    if (autoStart && initialDuration > 0) {
      startTimer(initialDuration);
    }
  }, [autoStart, initialDuration, startTimer]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  return {
    timeLeft,
    isActive,
    isPaused,
    startTimer,
    pauseTimer,
    resumeTimer,
    resetTimer,
    // Utility functions
    setDuration: (duration) => {
      gameDurationRef.current = duration * 1000;
      if (!isActive) {
        setTimeLeft(duration);
      }
    },
    // Get elapsed time in seconds
    getElapsedTime: () => {
      if (!gameStartTimeRef.current) return 0;
      return Math.floor((Date.now() - gameStartTimeRef.current) / 1000);
    },
    // Get remaining time as percentage
    getProgress: () => {
      const total = gameDurationRef.current / 1000;
      return total > 0 ? Math.max(0, (timeLeft / total) * 100) : 0;
    }
  };
};